import { useMutation } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { downloadProgramBase } from './download-program-base';
import type { ProgramBaseType } from './program-export-availability';

/**
 * EU-654 — exportação de bases por programa com TOAST DE 3 ESTADOS
 * (RN-06..10): "Preparando base" (loading, id fixo por programa) →
 * "Base de respostas baixada" (Fechar) | "Erro inesperado" (Fechar). O toast
 * é o MESMO elemento mudando de estado (id compartilhado) e vive no canto
 * inferior direito (posição default do Toaster — guard V53). Não bloqueia a
 * UI: o consultor segue navegando enquanto o backend prepara (EXP-025).
 *
 * EU-739 — o arquivo passou a ser GERADO NO BACKEND (spec 080): o hook baixa
 * o blob pronto em vez de buscar um JSON O(nº de inscritos) e montar o XLSX no
 * browser. Estados do toast preservados; a base VAZIA (programa sem inscritos)
 * ganha aviso próprio (FR-020a) e o disparo duplo é barrado NA ORIGEM: um
 * export pendente por PROGRAMA — o toast tem id por programa, dois tipos
 * simultâneos do mesmo programa disputariam o mesmo elemento (FR-013).
 */
/**
 * Bug QA EU-654 (spinner infinito no estado concluído): o sonner atualiza o
 * toast por id fazendo MERGE ({...toast, ...data}) e o `toast()` genérico não
 * envia `type` — o `type: 'loading'` do "Preparando base" sobrevivia ao update
 * e o spinner girava para sempre ao lado de "Base de respostas baixada". A
 * chave EXPLÍCITA `type: undefined` sobrescreve o loading no merge e devolve o
 * toast neutro (RN-08: estado concluído/erro SEM spinner). O sonner não expõe
 * `type` em `ExternalToast`, por isso o reset entra por spread (o excess
 * property check do TS não se aplica a spreads) — não trocar por
 * `toast.success`/`toast.error`, que injetam ícone fora do padrão neutro do
 * Figma (guard V53).
 */
const RESET_LOADING_TYPE = { type: undefined };

/**
 * Programas com export EM ANDAMENTO neste tab (EXP-038 — clique duplo, e o
 * segundo tipo do mesmo programa enquanto o primeiro roda). Module-level de
 * propósito: qualquer instância do hook (listagem, editor) enxerga o mesmo
 * registro. Entre abas/sessões quem serializa é o advisory lock do backend.
 */
const pendingPrograms = new Set<string>();

export function useExportProgramBase() {
  const mutation = useMutation({
    mutationFn: async (input: { programId: string; type: ProgramBaseType }) => {
      return downloadProgramBase(input.programId, input.type);
    },
    onMutate: (input) => {
      pendingPrograms.add(input.programId);
      const toastId = `program-export-${input.programId}`;
      toast.loading('Preparando base', { id: toastId, duration: Infinity });
      return { toastId };
    },
    onSuccess: (result, _input, context) => {
      const toastId = context?.toastId ?? 'program-export';
      // FR-020a — base vazia é SUCESSO com aviso próprio, distinto do erro.
      const message =
        result.dataRows === 0
          ? 'Base baixada sem inscritos — o programa não tem nenhuma inscrição ativa'
          : 'Base de respostas baixada';
      toast(message, {
        id: toastId,
        duration: Infinity,
        action: { label: 'Fechar', onClick: () => toast.dismiss(toastId) },
        ...RESET_LOADING_TYPE,
      });
    },
    onError: (_err, _input, context) => {
      const toastId = context?.toastId ?? 'program-export';
      toast('Erro inesperado', {
        id: toastId,
        duration: Infinity,
        action: { label: 'Fechar', onClick: () => toast.dismiss(toastId) },
        ...RESET_LOADING_TYPE,
      });
    },
    onSettled: (_data, _err, input) => {
      pendingPrograms.delete(input.programId);
    },
  });

  return {
    ...mutation,
    /**
     * Dispara a extração, IGNORANDO o disparo redundante do mesmo programa
     * (FR-013 no cliente): o toast daquele programa já está em
     * `Preparando base`; empilhar outra geração só desperdiçaria backend.
     */
    mutate: (input: { programId: string; type: ProgramBaseType }) => {
      if (pendingPrograms.has(input.programId)) return;
      mutation.mutate(input);
    },
  };
}
